// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared hook for fetching Digital Twin site data.
 * Used by DCAssets and KnowledgeGraph pages.
 *
 * Backed by react-query (ADR-0008), so the graph lives in a shared cache rather
 * than component-local state: Knowledge Graph ↔ Assets navigation joins the
 * cached (or in-flight) request instead of restarting the paginated fetch, and
 * `useTwinPrefetch` can start the load as soon as a site resolves.
 */

import { useCallback, useEffect } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { getDatacenterDetails } from '../services/digitalTwinService';
import { TWIN_STALE_TIME_MS } from '../config/queryConfig';
import { useAuth } from './useAuth';
import type { Entity, Relationship } from '../types/digitalTwin';

/**
 * Which sections the twin API returns. `'state'` carries live telemetry;
 * `''` explicitly omits it, which is the cheaper structural-only payload used
 * for first paint (see the two-phase load below).
 */
export type TwinInclude = '' | 'state';

/** Page size for the cursor-paginated details endpoint (upstream max). */
const PAGE_LIMIT = 5000;

/**
 * Most pages one load will follow. The largest site is a few thousand
 * entities, so this is far above any real graph: it exists so a cursor the
 * upstream never exhausts (a bug that repeats or regenerates it) stops with
 * what was loaded instead of fetching forever.
 */
export const MAX_TWIN_PAGES = 20;

/** The whole site graph, flattened across however many pages it took. */
export interface TwinGraph {
  siteId: string;
  entities: Entity[];
  relationships: Relationship[];
  /** Paging stopped early (page cap, or a cursor that never advanced): the graph is partial. */
  truncated: boolean;
}

/**
 * Follow cursor-based pagination until the API stops returning a nextCursor —
 * large sites can have thousands of entities spread across many pages (page
 * size is capped upstream), so a single request only ever returns a partial
 * graph. The `signal` is react-query's, so an abandoned load genuinely aborts
 * mid-pagination instead of running to completion against a dead observer.
 */
async function fetchTwinGraph(
  siteId: string,
  include: TwinInclude,
  signal?: AbortSignal
): Promise<TwinGraph> {
  const entities: Entity[] = [];
  const relationships: Relationship[] = [];
  let graphSiteId = '';
  let cursor: string | undefined;
  let truncated = false;

  for (let pages = 0; ; pages += 1) {
    const page = await getDatacenterDetails(
      siteId,
      { include, limit: PAGE_LIMIT, cursor },
      { signal }
    );
    graphSiteId = page.siteId;
    entities.push(...(page.entities ?? []));
    relationships.push(...(page.relationships ?? []));
    const next = page.page?.nextCursor;
    if (!next) break;
    // A cursor that does not advance would re-read the same page forever.
    if (next === cursor || pages + 1 >= MAX_TWIN_PAGES) {
      console.warn(`Digital Twin paging for ${siteId} stopped after ${pages + 1} pages`);
      truncated = true;
      break;
    }
    cursor = next;
  }

  return { siteId: graphSiteId, entities, relationships, truncated };
}

/**
 * Query options for one phase of a site's twin graph. Shared by the hook and
 * `useTwinPrefetch` so the two can never disagree about the cache key.
 *
 * The key carries the OMS `site_id`, the same id the app's routes use. The BFF
 * authorizes that id and the twin knows the site by it too, so no site key is
 * involved.
 */
export function twinQueryOptions(siteId: string, include: TwinInclude) {
  return queryOptions({
    queryKey: ['digital-twin', siteId, include] as const,
    queryFn: ({ signal }) => fetchTwinGraph(siteId, include, signal),
    staleTime: TWIN_STALE_TIME_MS,
  });
}

export interface UseDigitalTwinOptions {
  /**
   * When set, silently re-fetches the live graph on this interval (ms) to
   * refresh telemetry. The interval is per-observer: it starts when a twin page
   * mounts and stops when it unmounts, while the cache survives.
   */
  pollIntervalMs?: number;
}

export interface UseDigitalTwinResult {
  entities: Entity[];
  relationships: Relationship[];
  siteId: string;
  loading: boolean;
  error: string | null;
  /** The loaded graph is partial: paging stopped before the upstream ran out of pages. */
  truncated: boolean;
  refetch: () => void;
}

const NO_ENTITIES: Entity[] = [];
const NO_RELATIONSHIPS: Relationship[] = [];

export function useDigitalTwin(
  siteId: string | undefined,
  options: UseDigitalTwinOptions = {}
): UseDigitalTwinResult {
  const { pollIntervalMs } = options;
  const { sites } = useAuth();
  const twinSiteId = siteId && sites.some(s => s.site_id === siteId) ? siteId : undefined;
  const enabled = !!twinSiteId;

  const live = useQuery({
    ...twinQueryOptions(twinSiteId ?? '', 'state'),
    enabled,
    refetchInterval: pollIntervalMs ?? false,
  });

  const structural = useQuery({
    ...twinQueryOptions(twinSiteId ?? '', ''),
    enabled: enabled && live.data === undefined,
  });

  const graph = live.data ?? structural.data;

  useEffect(() => {
    if (graph && live.error) {
      console.warn('Digital Twin telemetry poll failed:', live.error);
    }
  }, [graph, live.error]);

  const failure = graph ? null : (structural.error ?? live.error);

  const structuralRefetch = structural.refetch;
  const liveRefetch = live.refetch;
  const refetch = useCallback(() => {
    if (!enabled) return;
    void structuralRefetch();
    void liveRefetch();
  }, [enabled, structuralRefetch, liveRefetch]);

  return {
    entities: graph?.entities ?? NO_ENTITIES,
    relationships: graph?.relationships ?? NO_RELATIONSHIPS,
    siteId: graph?.siteId ?? '',
    loading: enabled && !graph && !failure,
    error: failure ? (failure as Error).message || 'Failed to load site data' : null,
    truncated: graph?.truncated ?? false,
    refetch,
  };
}
